import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircleQuestion } from "lucide-react";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { useCentralStore } from "../../store/Central";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { useConnectStore } from "../../store/Connect";
import { useFormsStore } from "../../store/Forms";
import { InboxWidget } from "@/components/src/inbox/inbox-widget.component";
import {
	DeliveriesInboxWidget,
	ActiveDelivery,
} from "@/components/src/deliveries-inbox/deliveries-inbox-widget.component";
import {
	UnpaidInvoicesWidget,
	UnpaidInvoice,
} from "@/components/src/unpaid-invoices/unpaid-invoices-widget.component";
import { FaqAccordion } from "@/components/src/faq/faq-accordion.component";
import { VendorListWidget } from "@/components/src/vendor-list/vendor-list-widget.component";
import { RecentsStrip } from "@/components/src/recents-strip/recents-strip.component";
import { supabase } from "@/lib/supabase";
import { getRecents } from "@/lib/utils/recents";
import { getGreeting } from "@/lib/utils/greeting";
import { pageToIcon } from "../../layout/layout-components/app-bar/app-bar-components/app-bar-lists/page-to-icon";
import { SubscriptionBanner } from "../../layout/layout-components/app-bar/app-bar-components/subscription-banner.component";
import { PasswordBanner } from "../../layout/layout-components/app-bar/app-bar-components/password-banner.component";
import { DesktopUpdateBanner } from "../../layout/layout-components/app-bar/app-bar-components/desktop-update-banner.component";
import { QuickstartDashboardBanner } from "../../components/subscription/quickstart-dashboard-banner.component";

export const CommDashboardPage: React.FC = () => {
	const navigate = useNavigate();
	const [unpaidInvoices, setUnpaidInvoices] = useState<UnpaidInvoice[]>([]);
	const [activeDeliveries, setActiveDeliveries] = useState<ActiveDelivery[]>(
		[]
	);

	const profile = useCentralStore((s) => s.profile);
	const organization = useCentralStore((s) => s.organization);

	const latestChannels = useRealtimeStore((s) => s.latestChannels);
	const getNotificationNavigationPath = useRealtimeStore(
		(s) => s.getNotificationNavigationPath
	);
	const updateChannelReadState = useRealtimeStore(
		(s) => s.updateChannelReadState
	);
	const profilesForChat = useRealtimeStore((s) => s.profilesForChat);

	const connectedVendors = useConnectStore((s) => s.connectedVendors);
	const brandsByOrgId = useConnectStore((s) => s.brandsByOrgId);

	const formsByOrgId = useFormsStore((s) => s.formsByOrgId);
	const fetchForms = useFormsStore((s) => s.fetchForms);

	const hasVendors = connectedVendors.length > 0;

	useEffect(() => {
		if (connectedVendors.length > 0) {
			fetchForms(connectedVendors);
		}
	}, [connectedVendors]);

	const vendors = useMemo(
		() =>
			connectedVendors.map((id) => ({
				vendorId: id,
				name: brandsByOrgId[id]?.name || "Labor",
				forms: Object.values(formsByOrgId[id] || {})
					.filter((f) => f.published_at)
					.map((f) => ({
						id: f.id,
						title: f.title,
						bgColor: f.bg_color,
					})),
			})),
		[connectedVendors, brandsByOrgId, formsByOrgId]
	);

	const handleCreateJob = (vendorId: string, formId?: string) => {
		const params = formId ? `?formId=${formId}` : "";
		navigate(`/vendor/${vendorId}/job-request${params}`);
	};

	useEffect(() => {
		if (organization?.id && hasVendors) {
			fetchUnpaidInvoices();
			fetchActiveDeliveries();
		}
	}, [organization?.id, hasVendors]);

	const fetchUnpaidInvoices = async () => {
		if (!organization?.id) return;
		const { data } = await (supabase.rpc as any)(
			"get_unpaid_shared_invoices",
			{ p_customer_org_id: organization.id }
		);
		if (data) {
			setUnpaidInvoices(data as UnpaidInvoice[]);
		}
	};

	const fetchActiveDeliveries = async () => {
		if (!organization?.id) return;
		const { data } = await (supabase.rpc as any)(
			"get_active_shared_deliveries",
			{ p_customer_org_id: organization.id }
		);
		if (data) {
			setActiveDeliveries(
				(data as any[]).map((d) => ({
					delivery_id: d.delivery_id,
					job_id: d.job_id,
					job_code: d.job_code,
					job_title: d.job_title,
					status: d.status,
					courier_type: d.courier_type,
					courier_company_name: d.courier_company_name,
					description: d.description,
					scheduled_departure_at: d.scheduled_departure_at,
					created_at: d.created_at,
					vendor_org_id: d.vendor_org_id,
					vendor_name: d.vendor_name,
				}))
			);
		}
	};

	const handleDeliveryClick = (delivery: ActiveDelivery) => {
		if (delivery.vendor_org_id && delivery.job_id) {
			navigate(
				`/vendor/${delivery.vendor_org_id}/job/${delivery.job_id}`
			);
		}
	};

	const getOrgName = (orgId: string): string | undefined => {
		return brandsByOrgId[orgId]?.name || undefined;
	};

	const handleInvoiceClick = (invoice: UnpaidInvoice) => {
		if (invoice.vendor_org_id) {
			navigate(`/vendor/${invoice.vendor_org_id}/invoices`);
		}
	};

	const recents = getRecents().filter(
		(item) =>
			item.type !== "client" &&
			!(item.type === "page" && item.key === "dashboard-home")
	);

	const firstName = profile?.first_name || "";

	return (
		<div className="w-full h-full flex flex-col overflow-hidden">
			<HeaderPortal>
				<div className="flex items-center gap-3">
					<h2 className="text-lg text-gray-800">
						{getGreeting()}
						{firstName ? `, ${firstName}` : ""}.
					</h2>
					<span className="text-sm text-gray-400">
						{new Date().toLocaleDateString("de-CH", {
							weekday: "long",
							day: "numeric",
							month: "long",
							year: "numeric",
						})}
					</span>
				</div>
			</HeaderPortal>

			<RecentsStrip
				items={recents}
				pageIcon={(key) =>
					pageToIcon(key, {
						width: "16px",
						height: "16px",
						color: "rgb(100, 116, 139)",
					})
				}
			/>

			<div className="px-3 pt-3 flex flex-wrap gap-2">
				<SubscriptionBanner expanded={true} />
				<PasswordBanner expanded={true} />
				<DesktopUpdateBanner expanded={true} />
				<QuickstartDashboardBanner />
			</div>

			<div className="flex-1 min-h-0 p-3 grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-1 gap-3">
				<InboxWidget
					channels={latestChannels}
					getNavigationPath={
						getNotificationNavigationPath ??
						((ch) => `/chats/${ch.id}`)
					}
					onMarkAsRead={updateChannelReadState}
					organizationId={organization?.id ?? ""}
					getOrgName={getOrgName}
					userId={profile?.id ?? ""}
					profilesForChat={profilesForChat}
					className="min-h-0"
				/>

				<div className="min-h-0 flex flex-col gap-3">
					<VendorListWidget
						vendors={vendors}
						onCreateJob={handleCreateJob}
						title="Fremdlabore"
						emptyMessage="Noch keine Fremdlabore verbunden."
						className="flex-1 min-h-0"
					/>

					{hasVendors && (
						<DeliveriesInboxWidget
							deliveries={activeDeliveries}
							onDeliveryClick={handleDeliveryClick}
							className="flex-1 min-h-0"
						/>
					)}

					{hasVendors && (
						<UnpaidInvoicesWidget
							invoices={unpaidInvoices}
							onInvoiceClick={handleInvoiceClick}
							className="flex-1 min-h-0"
						/>
					)}

					<FaqAccordion className="bg-white rounded-lg border border-gray-200 p-3 flex-shrink-0" />

					<div
						className="flex items-center gap-2.5 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer transition-colors flex-shrink-0"
						onClick={() => window.Intercom?.("show")}
						data-testid="dashboard-support-link"
						aria-label="Support kontaktieren"
					>
						<MessageCircleQuestion className="h-4 w-4 text-gray-400" />
						<span className="text-sm text-gray-600">Support</span>
					</div>
				</div>
			</div>
		</div>
	);
};
